/*  ITER225 — Enriched Competitor Intelligence
 *  A pure data-acquisition + validation surface. Renders:
 *    1. Coverage summary  (Found / Enriched / Pending)
 *    2. Sortable + searchable table of enriched competitors
 *    3. Detail drawer  (menu categories, top items + prices, platform
 *                       data, raw payload, enrichment history)
 *
 *  NO business conclusions are rendered anywhere in this component.
 *  Conclusions belong in the AI / PDF layers.
 */
import React, { useMemo, useState } from "react";
import {
  Search, ChevronUp, ChevronDown, X, ExternalLink, CheckCircle2,
  Clock, Tag, Truck, ChefHat, Sparkles, Database, History, FileJson,
  Layers,
} from "lucide-react";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../ui/sheet";


// ── helpers ──────────────────────────────────────────────────────────
const fmtTs = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const ago = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};
const platformLabel = (arr) => {
  if (!arr || arr.length === 0) return "—";
  if (arr.length === 2) return "Both";
  return arr[0][0].toUpperCase() + arr[0].slice(1);
};

const renderSortableTh = ({ k, children, align, sort, onSort }) => (
  <th className={`py-2 px-2 text-${align || "left"} font-semibold text-slate-600 cursor-pointer select-none`}
      onClick={() => onSort(k)}
      data-testid={`enriched-sort-${k}`}>
    <span className="inline-flex items-center gap-1">
      {children}
      {sort.key === k && (sort.dir === "asc"
        ? <ChevronUp className="w-3 h-3"/>
        : <ChevronDown className="w-3 h-3"/>)}
    </span>
  </th>
);


// ── Main visibility view ────────────────────────────────────────────
export function EnrichedCompetitorsView({
  enrichmentMap, places, enrichmentHistory, byPlaceId,
}) {
  const coverage = enrichmentMap?.coverage || {
    competitors_found: places?.length || 0,
    competitors_enriched: 0,
    pending_enrichment: places?.length || 0,
  };
  const enrichedRows = useMemo(() => {
    const map = enrichmentMap?.by_place_id || {};
    const placeIndex = Object.fromEntries((places || [])
      .map(p => [p.place_id, p]));
    return Object.entries(map).map(([pid, e]) => {
      const place = placeIndex[pid] || {};
      const en = byPlaceId?.[pid] || {};
      const sig = en.signals || {};
      return {
        place_id:     pid,
        name:         e.name || place.name || "—",
        influence:    place._influence ?? sig.influence_score ?? null,
        rating:       e.rating ?? place.rating ?? null,
        reviews:      e.review_count ?? place.user_rating_count ?? null,
        p4t:          e.price_for_two ?? null,
        menu_count:   e.menu_item_count ?? 0,
        top_categories: e.top_categories || [],
        bestseller_items: e.bestseller_items || [],
        delivery:     !!e.delivery_supported,
        delivery_min: e.delivery_time_min ?? null,
        hours:        e.operating_hours || {},
        platforms:    e.source_platforms || [],
        enriched_at:  e.enriched_at,
        matched_by:   e.matched_by || null,
        is_stub:      !!e.is_stub,
        raw_records:  e.raw_records || {},
        full_menu:    e.full_menu || [],
        platform_links: e.platform_links || {},
        cuisines:     e.cuisines || [],
        history:      enrichmentHistory?.[pid] || [],
        _entry:       e,
        _place:       place,
      };
    });
  }, [enrichmentMap, places, enrichmentHistory, byPlaceId]);

  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "influence", dir: "desc" });
  const [drawerRow, setDrawerRow] = useState(null);

  const onSort = (key) => setSort(s =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
                  : { key, dir: "desc" });

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let rows = enrichedRows;
    if (term) {
      rows = rows.filter(r =>
        r.name?.toLowerCase().includes(term)
        || (r.top_categories || []).some(c => (c.name || "").toLowerCase().includes(term))
        || (r.bestseller_items || []).some(b => b.toLowerCase().includes(term))
        || (r.cuisines || []).some(c => c.toLowerCase().includes(term))
        || (r.platforms || []).some(p => p.includes(term))
      );
    }
    const cmp = (a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string") return av.localeCompare(bv);
      return av - bv;
    };
    return [...rows].sort((a, b) => sort.dir === "asc" ? cmp(a, b) : -cmp(a, b));
  }, [enrichedRows, q, sort]);

  const SortableTh = ({ k, children, align = "left" }) => renderSortableTh(
    { k, children, align, sort, onSort });

  return (
    <div className="space-y-4" data-testid="enriched-competitors-view">
      {/* ── Coverage row ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CoverageTile
          testid="enriched-coverage-found"
          label="Competitors Found"
          value={coverage.competitors_found}
          tone="slate"/>
        <CoverageTile
          testid="enriched-coverage-enriched"
          label="Competitors Enriched"
          value={coverage.competitors_enriched}
          tone="ok"/>
        <CoverageTile
          testid="enriched-coverage-pending"
          label="Pending Enrichment"
          value={coverage.pending_enrichment}
          tone={coverage.pending_enrichment > 0 ? "warn" : "ok"}/>
      </div>

      {/* ── Toolbar: search ──────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2"/>
          <Input
            placeholder="Search by competitor, category, dish, platform…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9"
            data-testid="enriched-search-input"
          />
        </div>
        <div className="text-xs text-slate-500" data-testid="enriched-row-count">
          Showing <b className="text-slate-700">{filtered.length}</b> of {enrichedRows.length} enriched
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────── */}
      {enrichedRows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/50
                        p-8 text-center text-sm text-slate-500"
             data-testid="enriched-empty-state">
          <Database className="w-6 h-6 mx-auto mb-2 text-slate-400"/>
          <div className="font-medium text-slate-700">
            No competitors enriched yet
          </div>
          <div className="text-xs mt-1">
            Run “Enrich Top 10” or “Enrich All Qualifying” from the panel above to
            populate this view with live menu, price & delivery data.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide">
              <tr>
                <SortableTh k="name">Competitor</SortableTh>
                <SortableTh k="influence" align="right">Influence</SortableTh>
                <SortableTh k="rating" align="right">Rating</SortableTh>
                <SortableTh k="reviews" align="right">Reviews</SortableTh>
                <SortableTh k="p4t" align="right">Price for Two</SortableTh>
                <SortableTh k="menu_count" align="right">Menu</SortableTh>
                <th className="py-2 px-2 text-left font-semibold text-slate-600">Top Categories</th>
                <th className="py-2 px-2 text-left font-semibold text-slate-600">Top 5 Bestsellers</th>
                <SortableTh k="delivery">Delivery</SortableTh>
                <SortableTh k="delivery_min" align="right">ETA</SortableTh>
                <th className="py-2 px-2 text-left font-semibold text-slate-600">Hours</th>
                <th className="py-2 px-2 text-left font-semibold text-slate-600">Platform</th>
                <SortableTh k="enriched_at">Last Enriched</SortableTh>
                <th className="py-2 px-2 text-left font-semibold text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody data-testid="enriched-table-body">
              {filtered.map(r => (
                <tr key={r.place_id}
                    onClick={() => setDrawerRow(r)}
                    className="border-t border-slate-100 hover:bg-violet-50/30 cursor-pointer"
                    data-testid={`enriched-row-${r.place_id}`}>
                  <td className="py-1.5 px-2 text-slate-800 font-medium max-w-[220px]">
                    <div className="truncate">{r.name}</div>
                    {r.matched_by && (
                      <div className="text-[10px] text-slate-500"
                           data-testid={`enriched-matchedby-${r.place_id}`}>
                        matched by {r.matched_by.replace("_", " ")}
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono text-violet-700">
                    {r.influence != null ? r.influence : "—"}
                  </td>
                  <td className="py-1.5 px-2 text-right">{r.rating ?? "—"}</td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {r.reviews?.toLocaleString?.() ?? r.reviews ?? "—"}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono"
                      data-testid={`enriched-p4t-${r.place_id}`}>
                    {r.p4t ? `₹${Math.round(r.p4t)}` : "—"}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono"
                      data-testid={`enriched-menu-${r.place_id}`}>
                    {r.menu_count || "—"}
                  </td>
                  <td className="py-1.5 px-2 max-w-[180px]">
                    <div className="flex flex-wrap gap-0.5">
                      {(r.top_categories || []).slice(0, 3).map((c, i) => (
                        <Badge key={i} variant="outline"
                               className="text-[10px] border-slate-200 text-slate-700">
                          {c.name} · {c.count}
                        </Badge>
                      ))}
                      {(r.top_categories || []).length === 0 && "—"}
                    </div>
                  </td>
                  <td className="py-1.5 px-2 max-w-[200px]">
                    <div className="flex flex-wrap gap-0.5">
                      {(r.bestseller_items || []).slice(0, 5).map((b, i) => (
                        <Badge key={i}
                               className={i === 0
                                 ? "text-[10px] bg-amber-50 text-amber-800 border border-amber-200"
                                 : "text-[10px] bg-slate-50 text-slate-700 border border-slate-200"}>
                          {b}
                        </Badge>
                      ))}
                      {(r.bestseller_items || []).length === 0 && "—"}
                    </div>
                  </td>
                  <td className="py-1.5 px-2"
                      data-testid={`enriched-delivery-${r.place_id}`}>
                    {r.delivery
                      ? <span className="text-emerald-700 font-medium">Yes</span>
                      : <span className="text-slate-500">No</span>}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono">
                    {r.delivery_min ? `${r.delivery_min} min` : "—"}
                  </td>
                  <td className="py-1.5 px-2 text-[11px] text-slate-700">
                    {Object.keys(r.hours || {}).length > 0
                      ? `${Object.keys(r.hours).length} day${Object.keys(r.hours).length > 1 ? "s" : ""}`
                      : "—"}
                  </td>
                  <td className="py-1.5 px-2 capitalize"
                      data-testid={`enriched-platform-${r.place_id}`}>
                    {platformLabel(r.platforms)}
                  </td>
                  <td className="py-1.5 px-2 text-[11px] text-slate-700"
                      data-testid={`enriched-ts-${r.place_id}`}
                      title={fmtTs(r.enriched_at)}>
                    {ago(r.enriched_at)}
                  </td>
                  <td className="py-1.5 px-2"
                      data-testid={`enriched-status-${r.place_id}`}>
                    {r.is_stub ? (
                      <Badge className="bg-amber-50 text-amber-800 border border-amber-200">
                        Stub
                      </Badge>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                        <CheckCircle2 className="w-3 h-3"/>Enriched
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={14} className="py-6 text-center text-slate-500">
                  No competitors match “{q}”.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Detail drawer ──────────────────────────────────────────── */}
      <EnrichedCompetitorDetailDrawer
        open={!!drawerRow}
        row={drawerRow}
        onClose={() => setDrawerRow(null)}
      />
    </div>
  );
}


// ── Coverage tile ───────────────────────────────────────────────────
function CoverageTile({ label, value, tone = "slate", testid }) {
  const palette = {
    slate:  { bg: "bg-slate-50",   bord: "border-slate-200",   text: "text-slate-700" },
    ok:     { bg: "bg-emerald-50", bord: "border-emerald-200", text: "text-emerald-700" },
    warn:   { bg: "bg-amber-50",   bord: "border-amber-200",   text: "text-amber-700" },
  }[tone] || {};
  return (
    <div className={`rounded-lg border ${palette.bord} ${palette.bg} px-4 py-3 flex flex-col`}
         data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
        {label}
      </div>
      <div className={`text-2xl font-semibold ${palette.text} mt-0.5`}>
        {value ?? 0}
      </div>
    </div>
  );
}


// ── Detail drawer ───────────────────────────────────────────────────
function EnrichedCompetitorDetailDrawer({ open, row, onClose }) {
  const [showRaw, setShowRaw] = useState(false);

  // Group full menu by category (hooks must run unconditionally)
  const menuByCategory = useMemo(() => {
    if (!row) return {};
    const buckets = {};
    for (const m of (row.full_menu || [])) {
      const c = (m.category || "Uncategorised").trim() || "Uncategorised";
      (buckets[c] = buckets[c] || []).push(m);
    }
    Object.values(buckets).forEach(arr =>
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    return buckets;
  }, [row]);

  const topItemsByPrice = useMemo(() => {
    if (!row) return [];
    return [...(row.full_menu || [])]
      .filter(m => m.price != null && m.price > 0)
      .sort((a, b) => (b.price || 0) - (a.price || 0))
      .slice(0, 10);
  }, [row]);

  if (!row) return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-2xl"/>
    </Sheet>
  );

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto"
                    data-testid={`enriched-drawer-${row.place_id}`}>
        <SheetHeader className="space-y-1">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-lg" data-testid="enriched-drawer-title">
              {row.name}
            </SheetTitle>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"
                    data-testid="enriched-drawer-close">
              <X className="w-4 h-4"/>
            </button>
          </div>
          <SheetDescription className="flex items-center gap-2 flex-wrap text-xs">
            {(row.platforms || []).map(p => (
              <Badge key={p} variant="outline"
                     className="bg-emerald-50 text-emerald-700 border-emerald-200 capitalize">
                {p}
              </Badge>
            ))}
            {row.matched_by && (
              <span className="text-slate-500">
                · matched by {row.matched_by.replace("_", " ")}
              </span>
            )}
            <span className="text-slate-500 ml-auto">
              Last enriched · {fmtTs(row.enriched_at)}
            </span>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-5">
          {/* Summary grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <DrawerStat label="Price for Two" v={row.p4t ? `₹${Math.round(row.p4t)}` : "—"}
                        icon={<Tag className="w-3 h-3"/>}/>
            <DrawerStat label="Menu items"  v={row.menu_count || "—"}
                        icon={<ChefHat className="w-3 h-3"/>}/>
            <DrawerStat label="Delivery"
                        v={row.delivery ? (row.delivery_min ? `Yes · ${row.delivery_min} min` : "Yes") : "No"}
                        icon={<Truck className="w-3 h-3"/>}/>
            <DrawerStat label="Live rating"
                        v={row.rating != null ? `${row.rating} · ${row.reviews?.toLocaleString?.() || ""}` : "—"}
                        icon={<Sparkles className="w-3 h-3"/>}/>
          </div>

          {/* Categories */}
          {Object.keys(menuByCategory).length > 0 && (
            <DrawerSection title="Full menu categories"
                            icon={<Layers className="w-3.5 h-3.5"/>}
                            testid={`drawer-categories-${row.place_id}`}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 text-xs">
                {Object.entries(menuByCategory)
                       .sort((a, b) => b[1].length - a[1].length)
                       .map(([cat, items]) => (
                  <div key={cat} className="border border-slate-200 rounded px-2 py-1.5">
                    <div className="font-medium text-slate-800">{cat}</div>
                    <div className="text-[10px] text-slate-500">{items.length} item{items.length > 1 ? "s" : ""}</div>
                  </div>
                ))}
              </div>
            </DrawerSection>
          )}

          {/* Top items by price */}
          {topItemsByPrice.length > 0 && (
            <DrawerSection title="Top items by price"
                            icon={<Tag className="w-3.5 h-3.5"/>}
                            testid={`drawer-topitems-${row.place_id}`}>
              <div className="rounded border border-slate-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Item</th>
                      <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Category</th>
                      <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Platform</th>
                      <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topItemsByPrice.map((it, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1">
                          {it.is_bestseller && <Sparkles className="w-2.5 h-2.5 inline mr-1 text-amber-600"/>}
                          {it.name}
                        </td>
                        <td className="px-2 py-1 text-slate-600">{it.category || "—"}</td>
                        <td className="px-2 py-1 text-slate-600 capitalize">{it.platform}</td>
                        <td className="px-2 py-1 text-right font-mono">₹{Math.round(it.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DrawerSection>
          )}

          {/* Operating hours */}
          {Object.keys(row.hours || {}).length > 0 && (
            <DrawerSection title="Operating hours"
                            icon={<Clock className="w-3.5 h-3.5"/>}
                            testid={`drawer-hours-${row.place_id}`}>
              <div className="text-xs space-y-0.5">
                {Object.entries(row.hours).map(([day, slots]) => (
                  <div key={day} className="flex gap-2">
                    <span className="capitalize w-20 text-slate-500">{day}</span>
                    <span className="text-slate-800">
                      {(slots || []).map(s => Array.isArray(s) ? s.join("–") : s).join(", ") || "Closed"}
                    </span>
                  </div>
                ))}
              </div>
            </DrawerSection>
          )}

          {/* Platform-wise breakdown */}
          {Object.keys(row.raw_records || {}).length > 0 && (
            <DrawerSection title="Platform-wise data"
                            icon={<Database className="w-3.5 h-3.5"/>}
                            testid={`drawer-platforms-${row.place_id}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                {Object.entries(row.raw_records).map(([plat, rec]) => (
                  <div key={plat}
                       className="border border-slate-200 rounded p-2 space-y-0.5"
                       data-testid={`drawer-platform-${row.place_id}-${plat}`}>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline"
                             className="bg-emerald-50 text-emerald-700 border-emerald-200 capitalize">
                        {plat}
                      </Badge>
                      {rec.url && (
                        <a href={rec.url} target="_blank" rel="noreferrer"
                           className="text-sky-700 hover:underline inline-flex items-center gap-0.5">
                          Open <ExternalLink className="w-3 h-3"/>
                        </a>
                      )}
                    </div>
                    <KvLine k="Price for Two" v={rec.price_for_two ? `₹${Math.round(rec.price_for_two)}` : "—"}/>
                    <KvLine k="Menu items"    v={rec.menu_item_count ?? "—"}/>
                    <KvLine k="Rating"        v={rec.rating ?? "—"}/>
                    <KvLine k="Reviews"       v={rec.review_count?.toLocaleString?.() ?? rec.review_count ?? "—"}/>
                    <KvLine k="Delivery"
                             v={rec.delivery_supported
                                ? (rec.delivery_time_min ? `Yes · ${rec.delivery_time_min}m` : "Yes")
                                : "No"}/>
                    <KvLine k="Coords"
                             v={(rec.lat != null && rec.lng != null) ? `${rec.lat}, ${rec.lng}` : "—"}/>
                    <KvLine k="Fetched at" v={fmtTs(rec.fetched_at)}/>
                  </div>
                ))}
              </div>
            </DrawerSection>
          )}

          {/* Raw payload */}
          <DrawerSection title="Raw enrichment payload"
                          icon={<FileJson className="w-3.5 h-3.5"/>}
                          testid={`drawer-raw-${row.place_id}`}>
            <button
              onClick={() => setShowRaw(s => !s)}
              className="text-xs text-sky-700 hover:underline mb-2"
              data-testid={`drawer-raw-toggle-${row.place_id}`}>
              {showRaw ? "Hide raw JSON" : "View raw"}
            </button>
            <pre className="text-[10px] bg-slate-900 text-slate-100 rounded p-2 max-h-72 overflow-auto"
                 data-testid={`drawer-raw-pre-${row.place_id}`}>
              {showRaw
                ? JSON.stringify(row.raw_records, null, 2)
                : JSON.stringify({
                    name: row.name,
                    source_platforms: row.platforms,
                    price_for_two: row.p4t,
                    menu_item_count: row.menu_count,
                    top_categories: row.top_categories,
                    bestseller_items: row.bestseller_items,
                    delivery: row.delivery,
                    delivery_time_min: row.delivery_min,
                    cuisines: row.cuisines,
                    rating: row.rating,
                    review_count: row.reviews,
                    enriched_at: row.enriched_at,
                    matched_by: row.matched_by,
                    platform_links: row.platform_links,
                  }, null, 2)}
            </pre>
          </DrawerSection>

          {/* Enrichment history */}
          <DrawerSection title="Enrichment history"
                          icon={<History className="w-3.5 h-3.5"/>}
                          testid={`drawer-history-${row.place_id}`}>
            {(row.history || []).length === 0 ? (
              <div className="text-xs text-slate-500 italic">
                Only the current run is on record. Future enrichment runs will
                accumulate here (last 5 retained).
              </div>
            ) : (
              <ul className="space-y-1.5 text-xs">
                {[...row.history].reverse().map((h, i) => (
                  <li key={i}
                      className="border-l-2 border-emerald-300 pl-2 py-0.5"
                      data-testid={`drawer-history-row-${row.place_id}-${i}`}>
                    <div className="text-slate-800">
                      <b>{fmtTs(h.ts)}</b>
                      <span className="text-slate-500"> · {h.scope || "—"}</span>
                    </div>
                    <div className="text-slate-600 text-[11px]">
                      P4T ₹{h.p4t ? Math.round(h.p4t) : "—"} · menu {h.menu_count ?? "—"}
                      {h.cost_usd != null && ` · cost $${(+h.cost_usd).toFixed(2)}`}
                      {h.cache_hits != null && ` · ${h.cache_hits} cache hits`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </DrawerSection>
        </div>
      </SheetContent>
    </Sheet>
  );
}


function DrawerStat({ label, v, icon }) {
  return (
    <div className="border border-slate-200 rounded px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
        {icon}{label}
      </div>
      <div className="text-sm font-medium text-slate-800 mt-0.5">{v}</div>
    </div>
  );
}

function DrawerSection({ title, icon, testid, children }) {
  return (
    <section data-testid={testid}>
      <h4 className="text-xs uppercase tracking-wide text-slate-600 font-semibold
                     flex items-center gap-1.5 mb-1.5">
        {icon}{title}
      </h4>
      {children}
    </section>
  );
}

function KvLine({ k, v }) {
  return (
    <div className="flex justify-between text-[11px]">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-800 font-medium">{v}</span>
    </div>
  );
}

export default EnrichedCompetitorsView;
